import type { BankPaymentItem } from '@/types/bank-payment.type'

import { toast } from 'sonner'
import { sleep } from '@/utils/sleep'
import { useForm } from 'react-hook-form'
import { fDateTime } from '@/utils/dayjs'
import { uploadFile } from '@/lib/firebase'
import { fError } from '@/utils/format-error'
import { Input } from '@/components/ui/input'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useBoolean } from '@/hooks/use-boolean'
import { getBankLabel } from '@/utils/common.utils'
import { FileUpload } from '@/sections/articles/types'
import { BANK_OPTIONS } from '@/constant/bank-options'
import { Card, CardContent } from '@/components/ui/card'
import React, { useState, useEffect, useRef } from 'react'
import CustomPagination from '@/components/pagination/custom-pagination'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingDialog from '@/components/share-components/loading-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Edit, Trash2, Plus, ImageOff, Banknote, Loader2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'

interface BankPaymentForm {
  bankName: string
  bankAccountName: string
  bankAccountNumber: string
  qrCodeImageUrl: string
  isDefault: boolean
}

const PAGE_SIZE = 10

const DEFAULT_FORM = {
  bankName: '',
  bankAccountName: '',
  bankAccountNumber: '',
  qrCodeImageUrl: '',
  isDefault: false,
}

const BankPaymentView: React.FC = () => {
  const loading = useBoolean(true)
  const submitting = useBoolean(false)
  const deleting = useBoolean(false)
  const loadingToggleIsDefault = useBoolean(false)
  const openCreateModal = useBoolean(false)
  const openEditModal = useBoolean(false)

  const [bankPayments, setBankPayments] = useState<BankPaymentItem[]>([])
  const [editingData, setEditingData] = useState<BankPaymentItem | null>(null)
  const [imageFile, setImageFile] = useState<FileUpload | null>(null)
  const [pageMeta, setPageMeta] = useState({
    total: 0,
    page: 1,
    limit: PAGE_SIZE,
  })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: '',
    bankAccountNumber: '',
  })

  const form = useForm<BankPaymentForm>({ defaultValues: DEFAULT_FORM })

  // Fetch data
  const fetchBankPayments = async (forcePage?: number) => {
    try {
      loading.onTrue()
      const { data } = await axios.get(endpoints.bankPayment.list, {
        params: {
          page: typeof forcePage === 'number' ? forcePage : pageMeta.page,
          limit: pageMeta.limit,
        },
      })

      setBankPayments(data.items || data)
      if (data.total !== undefined) {
        setPageMeta((prev) => ({
          ...prev,
          total: data.total,
          page: typeof forcePage === 'number' ? forcePage : prev.page,
        }))
      }
    } catch (error) {
      console.error('Error fetching QR codes:', error)
      toast.error(fError(error))
      setBankPayments([])
    } finally {
      loading.onFalse()
    }
  }

  useEffect(() => {
    fetchBankPayments()
  }, [pageMeta.page])

  useEffect(() => {
    if (pageMeta.page !== 1 && bankPayments.length === 0) {
      fetchBankPayments(pageMeta.page - 1)
    }
  }, [pageMeta.page, bankPayments.length])

  // Handle image upload and preview
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Create file upload object
      const fileUpload: FileUpload = {
        url: file,
        fileName: `qr-code-${Date.now()}.${file.name.split('.').pop()}`,
      }
      setImageFile(fileUpload)
    } else {
      setImageFile(null)
    }
  }

  // Get preview URL for display
  const getPreviewUrl = () => {
    if (!imageFile) return null
    if (typeof imageFile.url === 'string') {
      return imageFile.url
    } else {
      return URL.createObjectURL(imageFile.url)
    }
  }

  // Create data
  const handleCreate = async (data: BankPaymentForm) => {
    try {
      submitting.onTrue()
      let imageUrl = ''
      if (imageFile) {
        if (typeof imageFile.url === 'string') {
          imageUrl = imageFile.url
        } else {
          imageUrl = await uploadFile(
            imageFile.url,
            'bank-payment-qr-codes',
            imageFile.fileName,
          )
        }
      }

      const payload = {
        ...data,
        qrCodeImageUrl: imageUrl,
      }

      await axios.post(endpoints.bankPayment.create, payload)
      toast.success('เพิ่มบัญชี สำเร็จ')
      openCreateModal.onFalse()
      form.reset(DEFAULT_FORM)
      setImageFile(null)
      fetchBankPayments(1)
    } catch (error) {
      console.error('Error creating:', error)
      toast.error(fError(error))
    } finally {
      submitting.onFalse()
    }
  }

  // Update data
  const handleUpdate = async (data: BankPaymentForm) => {
    if (!editingData) return

    try {
      submitting.onTrue()
      let imageUrl = ''
      if (imageFile) {
        if (typeof imageFile.url === 'string') {
          imageUrl = imageFile.url
        } else {
          imageUrl = await uploadFile(
            imageFile.url,
            'bank-payment-qr-codes',
            imageFile.fileName,
          )
        }
      }

      const payload = {
        ...data,
        qrCodeImageUrl: imageUrl,
      }

      const { data: resData } = await axios.patch(
        endpoints.bankPayment.update(editingData.id),
        payload,
      )
      toast.success('อัปเดตบัญชี สำเร็จ')
      openEditModal.onFalse()
      setEditingData(null)
      form.reset(DEFAULT_FORM)
      setImageFile(null)

      if (resData) {
        setBankPayments((prev) =>
          prev.map((item) => {
            if (item.id === resData.id) {
              return resData
            }

            return item
          }),
        )
      } else {
        fetchBankPayments()
      }
    } catch (error) {
      console.error('Error updating:', error)
      toast.error(fError(error))
    } finally {
      submitting.onFalse()
    }
  }

  // Delete data
  const handleDelete = async (id: string) => {
    try {
      deleting.onTrue()
      await sleep(500)
      await axios.delete(endpoints.bankPayment.delete(id))
      toast.success('ลบบัญชี สำเร็จ')
      fetchBankPayments()
    } catch (error) {
      console.error('Error deleting:', error)
      toast.error(fError(error))
    } finally {
      deleting.onFalse()
    }
  }

  const handleToggleIsDefault = async (id: string, value: boolean) => {
    try {
      loadingToggleIsDefault.onTrue()

      const { data } = await axios.get(endpoints.bankPayment.list, {
        params: {
          page: 1,
          limit: 10,
          isDefault: true,
        },
      })
      const oldDefaultId = data?.items[0]?.id

      const updateOldDefault = oldDefaultId
        ? axios.patch(endpoints.bankPayment.update(oldDefaultId), {
            isDefault: false,
          })
        : Promise.resolve()

      const updateNewDefault = axios.patch(endpoints.bankPayment.update(id), {
        isDefault: value,
      })

      await Promise.all([updateOldDefault, updateNewDefault])

      toast.success('ตั้งค่าเริ่มต้นสำเร็จ')

      setBankPayments((prev) =>
        prev.map((item) => {
          if (item.id === oldDefaultId) {
            return { ...item, isDefault: !value }
          }
          if (item.id === id) {
            return { ...item, isDefault: value }
          }

          return item
        }),
      )
    } catch (error) {
      console.error('Error toggle:', error)
      toast.error(fError(error))
    } finally {
      loadingToggleIsDefault.onFalse()
    }
  }

  // Open create modal
  const handleOpenCreateModal = () => {
    form.reset(DEFAULT_FORM)
    setImageFile(null)
    openCreateModal.onTrue()
  }

  // Open edit modal
  const handleOpenEditModal = (item: BankPaymentItem) => {
    setEditingData(item)
    form.reset({
      bankName: item.bankName,
      bankAccountName: item.bankAccountName,
      bankAccountNumber: item.bankAccountNumber,
      qrCodeImageUrl: item.qrCodeImageUrl,
      isDefault: item.isDefault,
    })

    // Set existing image for editing
    setImageFile(
      item.qrCodeImageUrl
        ? {
            url: item.qrCodeImageUrl,
            fileName: 'qr-code-image',
          }
        : null,
    )

    openEditModal.onTrue()
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    setPageMeta((prev) => ({ ...prev, page }))
  }

  // Form submit handler
  const onSubmit = (data: BankPaymentForm) => {
    if (editingData) {
      handleUpdate(data)
    } else {
      handleCreate(data)
    }
  }

  // Close modals
  const handleCloseCreateModal = () => {
    openCreateModal.onFalse()
    form.reset(DEFAULT_FORM)
    setImageFile(null)
  }

  const handleCloseEditModal = () => {
    openEditModal.onFalse()
    setEditingData(null)
    form.reset(DEFAULT_FORM)
    setImageFile(null)
  }

  const previewUrl = getPreviewUrl()

  return (
    <div className="w-full">
      <LoadingSection loading={false}>
        <div className="space-y-6 mb-8">
          {/* Header */}
          <Card>
            <CardContent>
              <div className="flex items-center justify-between py-4">
                <div>
                  <h2 className="text-lg font-semibold text-black">
                    จัดการบัญชีสำหรับการรับเงิน
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    ตั้งค่าบัญชีสำหรับการรับชำระเงิน
                  </p>
                </div>
                <Button onClick={handleOpenCreateModal}>
                  <Plus className="mr-2 h-4 w-4" />
                  เพิ่มบัญชี
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Table Section */}
          <Card className="p-0">
            <CardContent className="p-0">
              {loading.value ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>QR Code</TableHead>
                      <TableHead>ธนาคาร</TableHead>
                      <TableHead>ชื่อบัญชี</TableHead>
                      <TableHead>เลขบัญชี</TableHead>
                      <TableHead>ตั้งค่าเริ่มต้น</TableHead>
                      <TableHead>วันที่สร้าง</TableHead>
                      <TableHead className="text-center">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <div className="h-12 w-12 bg-gray-200 rounded animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2 justify-center">
                            <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
                            <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : bankPayments.length === 0 ? (
                <div className="border rounded p-8 text-center">
                  <Banknote className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-base text-muted-foreground">
                    ยังไม่มีบัญชี
                  </p>
                  <Button className="mt-4" onClick={handleOpenCreateModal}>
                    <Plus className="mr-2 h-4 w-4" />
                    สร้างบัญชี
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>QR Code</TableHead>
                      <TableHead>ธนาคาร</TableHead>
                      <TableHead>ชื่อบัญชี</TableHead>
                      <TableHead>เลขบัญชี</TableHead>
                      <TableHead>ตั้งค่าเริ่มต้น</TableHead>
                      <TableHead>วันที่สร้าง</TableHead>
                      <TableHead className="text-center">จัดการ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bankPayments.map((bankPayment) => (
                      <TableRow key={bankPayment.id}>
                        {/* QR Code Image */}
                        <TableCell>
                          <Avatar className="size-12 rounded ring-1 ring-gray-200">
                            <AvatarImage
                              src={bankPayment.qrCodeImageUrl}
                              alt={`QR Code for ${bankPayment.bankAccountName}`}
                              className="object-cover"
                            />
                            <AvatarFallback className="size-12 rounded bg-gray-100 text-gray-600">
                              <ImageOff />
                            </AvatarFallback>
                          </Avatar>
                        </TableCell>

                        {/* Bank Name */}
                        <TableCell>
                          <span className="font-medium">
                            {getBankLabel(bankPayment.bankName)}
                          </span>
                        </TableCell>

                        {/* Account Name */}
                        <TableCell>
                          <span className="font-medium">
                            {bankPayment.bankAccountName}
                          </span>
                        </TableCell>

                        {/* Account Number */}
                        <TableCell>
                          <span className="font-mono text-sm">
                            {bankPayment.bankAccountNumber}
                          </span>
                        </TableCell>

                        {/* Status */}
                        <TableCell className="text-center">
                          <Switch
                            className="scale-[0.8]"
                            disabled={loadingToggleIsDefault.value}
                            checked={bankPayment.isDefault}
                            onCheckedChange={(e) => {
                              if (!bankPayment.isDefault) {
                                handleToggleIsDefault(bankPayment.id, e)
                              }
                            }}
                          />
                        </TableCell>

                        {/* Created Date */}
                        <TableCell>
                          <span className="text-sm text-gray-500">
                            {fDateTime(bankPayment.createdAt)}
                          </span>
                        </TableCell>

                        {/* Actions */}
                        <TableCell className="text-center">
                          <div className="flex gap-2 justify-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenEditModal(bankPayment)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() =>
                                setConfirmDelete({
                                  open: true,
                                  id: bankPayment.id,
                                  bankAccountNumber:
                                    bankPayment.bankAccountNumber,
                                })
                              }
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {Math.ceil(pageMeta.total / pageMeta.limit) > 1 && (
            <CustomPagination
              currentPage={pageMeta.page}
              totalPages={Math.ceil(pageMeta.total / pageMeta.limit)}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </LoadingSection>

      {/* Create Dialog */}
      <Dialog
        open={openCreateModal.value}
        onOpenChange={handleCloseCreateModal}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>เพิ่มบัญชีใหม่</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* QR Code Image */}
              <FormField
                control={form.control}
                name="qrCodeImageUrl"
                rules={{ required: 'กรุณาอัปโหลดรูป QR Code' }}
                render={() => (
                  <FormItem>
                    <FormLabel>รูป QR Code</FormLabel>
                    <FormControl>
                      <Input
                        disabled={submitting.value}
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={(e) => {
                          form.setValue(
                            'qrCodeImageUrl',
                            e.target.files?.[0].name || 'qr-code-image',
                          )
                          form.clearErrors('qrCodeImageUrl')
                          handleImageChange(e)
                        }}
                      />
                    </FormControl>
                    {previewUrl && (
                      <Avatar className="size-32 mt-2 rounded ring-1 ring-gray-200">
                        <AvatarImage
                          src={previewUrl}
                          alt="QR Code Preview"
                          className="object-cover"
                        />
                        <AvatarFallback className="size-32 rounded bg-gray-100 text-gray-600">
                          <ImageOff />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Bank Name */}
              <FormField
                control={form.control}
                name="bankName"
                rules={{ required: 'กรุณาเลือกธนาคาร' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ธนาคาร</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={submitting.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="เลือกธนาคาร" />
                        </SelectTrigger>
                        <SelectContent>
                          {BANK_OPTIONS.map((bank) => (
                            <SelectItem key={bank.value} value={bank.value}>
                              {bank.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Account Name */}
              <FormField
                control={form.control}
                name="bankAccountName"
                rules={{ required: 'กรุณากรอกชื่อบัญชี' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ชื่อบัญชี</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="ชื่อบัญชีธนาคาร"
                        disabled={submitting.value}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Account Number */}
              <FormField
                control={form.control}
                name="bankAccountNumber"
                rules={{ required: 'กรุณากรอกเลขบัญชี' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>เลขบัญชี</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="เลขบัญชีธนาคาร"
                        disabled={submitting.value}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Is Default */}
              {/* <FormField
                control={form.control}
                name="isDefault"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded border p-3 shadow-sm">
                    <div className="space-y-0.5">
                      <FormLabel>ตั้งเป็นค่าเริ่มต้น</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        QR Code นี้จะถูกใช้เป็นตัวเลือกแรกในการรับเงิน
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              /> */}

              <DialogFooter>
                <Button
                  disabled={submitting.value}
                  type="button"
                  variant="outline"
                  onClick={handleCloseCreateModal}
                >
                  ยกเลิก
                </Button>
                <Button type="submit" disabled={submitting.value}>
                  {submitting.value ? (
                    <>
                      <Loader2 className="animate-spin" /> กำลังโหลด..
                    </>
                  ) : (
                    'เพิ่มบัญชี'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditModal.value} onOpenChange={handleCloseEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>แก้ไขบัญชี</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* QR Code Image */}
              <FormField
                control={form.control}
                name="qrCodeImageUrl"
                rules={{ required: 'กรุณาอัปโหลดรูป QR Code' }}
                render={() => (
                  <FormItem>
                    <FormLabel>รูป QR Code</FormLabel>
                    <FormControl>
                      <Input
                        disabled={submitting.value}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          form.clearErrors('qrCodeImageUrl')
                          form.setValue('qrCodeImageUrl', 'dirty', {
                            shouldDirty: true,
                          })
                          handleImageChange(e)
                        }}
                      />
                    </FormControl>
                    {previewUrl && (
                      <Avatar className="size-32 mt-2 rounded ring-1 ring-gray-200">
                        <AvatarImage
                          src={previewUrl}
                          alt="QR Code Preview"
                          className="object-cover"
                        />
                        <AvatarFallback className="size-32 rounded bg-gray-100 text-gray-600">
                          <ImageOff />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Bank Name */}
              <FormField
                control={form.control}
                name="bankName"
                rules={{ required: 'กรุณาเลือกธนาคาร' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ธนาคาร</FormLabel>
                    <FormControl>
                      <Select
                        disabled={submitting.value}
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="เลือกธนาคาร" />
                        </SelectTrigger>
                        <SelectContent>
                          {BANK_OPTIONS.map((bank) => (
                            <SelectItem key={bank.value} value={bank.value}>
                              {bank.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Account Name */}
              <FormField
                control={form.control}
                name="bankAccountName"
                rules={{ required: 'กรุณากรอกชื่อบัญชี' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ชื่อบัญชี</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="ชื่อบัญชีธนาคาร"
                        disabled={submitting.value}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Account Number */}
              <FormField
                control={form.control}
                name="bankAccountNumber"
                rules={{ required: 'กรุณากรอกเลขบัญชี' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>เลขบัญชี</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="เลขบัญชีธนาคาร"
                        disabled={submitting.value}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Is Default */}
              {/* <FormField
                control={form.control}
                name="isDefault"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded border p-3 shadow-sm">
                    <div className="space-y-0.5">
                      <FormLabel>ตั้งเป็นค่าเริ่มต้น</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        QR Code นี้จะถูกใช้เป็นตัวเลือกแรกในการรับเงิน
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              /> */}

              <DialogFooter>
                <Button
                  disabled={submitting.value}
                  type="button"
                  variant="outline"
                  onClick={handleCloseEditModal}
                >
                  ยกเลิก
                </Button>
                <Button
                  type="submit"
                  disabled={!form.formState.isDirty || submitting.value}
                >
                  {submitting.value ? (
                    <>
                      <Loader2 className="animate-spin" /> กำลังโหลด..
                    </>
                  ) : (
                    'บันทึกการเปลี่ยนแปลง'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDelete.open}
        onOpenChange={(open) =>
          setConfirmDelete({
            open,
            id: '',
            bankAccountNumber: '',
          })
        }
        title="ยืนยันการลบ"
        description={`คุณต้องการลบบัญชี${confirmDelete?.bankAccountNumber ? `เลขที่ ${confirmDelete?.bankAccountNumber}` : ''} หรือไม่?`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={() => {
          if (confirmDelete.id) {
            handleDelete(confirmDelete.id)
          }
        }}
      />

      <LoadingDialog open={deleting.value} title="กำลังลบบัญชี..." />
    </div>
  )
}

export { BankPaymentView }
export default BankPaymentView
